/**
 * Story 6.5 (PE-122): pure logic backing the search page's tier badge,
 * facet, and ranking behaviour. Kept separate from `SearchPage.tsx` so it's
 * unit-testable without rendering the search context (mirrors
 * `packages/app/src/techdocs/versioning.ts` and `tiersCache.ts`).
 */
import { Entity } from '@backstage/catalog-model';
import { CATALOG_FILTER_EXISTS } from '@backstage/plugin-catalog-react';
import { Result, SearchDocument } from '@backstage/plugin-search-common';
import { parseTierConfig } from '../../techdocs/tiers/parseTierConfig';

const TECHDOCS_REF_ANNOTATION = 'backstage.io/techdocs-ref';

/**
 * The tier fields the Story 6.5 collator adds to TechDocs documents
 * (`packages/backend/src/modules/search/techDocsTierCollator.ts`). Absent on
 * software-catalog results and on any document from a collator predating
 * this story.
 */
export type TechDocsTierSearchDocument = SearchDocument & {
  kind?: string;
  namespace?: string;
  name?: string;
  tier?: string;
  tierLabel?: string;
  tierPriority?: number;
};

function tierPriorityOf(result: Result<SearchDocument>): number {
  const value = (result.document as TechDocsTierSearchDocument).tierPriority;
  return typeof value === 'number' ? value : 0;
}

/**
 * AC 6: rank higher-priority tiers first (lower `tierPriority` number),
 * default tier first, without overriding the search engine's relevance
 * ordering. `Array.prototype.sort` is stable (ES2019+), so results tied on
 * `tierPriority` — including every non-tier-aware result, which floats at
 * the neutral priority 0 — keep the engine's original relative order; only
 * an actual tier-priority difference moves a result.
 */
export function rankByTierPriority<T extends Result<SearchDocument>>(
  results: T[],
): T[] {
  return [...results].sort((a, b) => tierPriorityOf(a) - tierPriorityOf(b));
}

/**
 * AC 4: the badge is suppressed for the common case — a result from the
 * default tier of an entity that only has one tier (or no tier annotation
 * at all). Any non-default tier always gets a badge; the default tier gets
 * one too once its entity has published more than one tier, since sibling
 * results for the same page would otherwise be visually indistinguishable.
 * `allResults` is the current page of results only (Backstage search results
 * are paginated), which is an accepted scope limit for this signal.
 */
export function tierBadgeLabel(
  result: Result<SearchDocument>,
  allResults: Result<SearchDocument>[],
): string | null {
  const doc = result.document as TechDocsTierSearchDocument;
  if (typeof doc.tier !== 'string' || typeof doc.tierLabel !== 'string') {
    return null;
  }
  const isDefaultTier = tierPriorityOf(result) === 0;
  const siblingTierCount = new Set(
    allResults
      .map(r => r.document as TechDocsTierSearchDocument)
      .filter(
        d =>
          typeof d.tier === 'string' &&
          d.kind === doc.kind &&
          d.namespace === doc.namespace &&
          d.name === doc.name,
      )
      .map(d => d.tier),
  ).size;
  if (isDefaultTier && siblingTierCount <= 1) {
    return null;
  }
  return doc.tierLabel;
}

/**
 * The slice of the catalog API the tier facet needs (structural, matching
 * `tiersCache.ts`'s `CatalogEntityLookup` — no full `CatalogApi` mock
 * required in tests).
 */
export type CatalogEntityTierLookup = {
  getEntities(request: {
    fields: string[];
    filter: Record<string, typeof CATALOG_FILTER_EXISTS>;
  }): Promise<{ items: Pick<Entity, 'metadata'>[] }>;
};

/** A tier facet option: the id filters (`SearchFilter` matches on `tier`), the label displays. */
export type TierFacetValue = { value: string; label: string };

/**
 * AC 5: the tier facet's options, dynamically derived from the tiers
 * declared across every entity with published TechDocs — never a hardcoded
 * internal/customer/public list. Mirrors the existing "Entity" facet's
 * approach of deriving its options from a live catalog query rather than
 * the search index itself.
 */
export async function fetchTierFacetValues(
  catalogApi: CatalogEntityTierLookup,
): Promise<TierFacetValue[]> {
  const { items } = await catalogApi.getEntities({
    fields: ['metadata.annotations'],
    filter: {
      [`metadata.annotations.${TECHDOCS_REF_ANNOTATION}`]:
        CATALOG_FILTER_EXISTS,
    },
  });

  const labelById = new Map<string, string>();
  for (const entity of items) {
    const result = parseTierConfig(entity.metadata.annotations);
    if (!result.ok) {
      continue;
    }
    for (const tier of result.config.tiers) {
      if (!labelById.has(tier.id)) {
        labelById.set(tier.id, tier.label);
      }
    }
  }

  return [...labelById.entries()]
    .map(([value, label]) => ({ value, label }))
    .sort((a, b) => a.label.localeCompare(b.label));
}
